import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

const useMenuConfig = () => {
  const [menuConfig, setMenuConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  const fetchMenuConfig = useCallback(async () => {
    if (!user?.tenantCode) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`/api/menu-config/${user.tenantCode}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setMenuConfig(result.data);
      } else {
        throw new Error(result.message || 'Failed to fetch menu configuration');
      }
    } catch (err) {
      console.error('Error fetching menu config:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.tenantCode]);

  const updateMenuCustomization = useCallback(async (navigationType, itemId, customizations) => {
    if (!user?.tenantCode) {
      throw new Error('No tenant code available');
    }


    try {
      const response = await fetch(`/api/menu-config/${user.tenantCode}/customize`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          navigationType,
          itemId,
          customizations
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        // Refresh menu config after successful update
        await fetchMenuConfig();
        return true;
      } else {
        throw new Error(result.message || 'Failed to update menu customization');
      }
    } catch (err) {
      console.error('Error updating menu customization:', err);
      throw err;
    }
  }, [user?.tenantCode, fetchMenuConfig]);

  const resetMenuConfig = useCallback(async () => {
    if (!user?.tenantCode) {
      throw new Error('No tenant code available');
    }

    try {
      const response = await fetch(`/api/menu-config/${user.tenantCode}/reset`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        // Refresh menu config after successful reset
        await fetchMenuConfig();
        return true;
      } else {
        throw new Error(result.message || 'Failed to reset menu configuration');
      }
    } catch (err) {
      console.error('Error resetting menu config:', err);
      throw err;
    }
  }, [user?.tenantCode, fetchMenuConfig]);

  const getAvailableMenuItems = useCallback(async () => {
    if (!user?.tenantCode) {
      throw new Error('No tenant code available');
    }

    try {
      const response = await fetch(`/api/menu-config/${user.tenantCode}/available`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        return result.data;
      } else {
        throw new Error(result.message || 'Failed to fetch available menu items');
      }
    } catch (err) {
      console.error('Error fetching available menu items:', err);
      throw err;
    }
  }, [user?.tenantCode]);

  useEffect(() => {
    fetchMenuConfig();
  }, [fetchMenuConfig]);

  return {
    menuConfig,
    loading,
    error,
    refetch: fetchMenuConfig,
    updateMenuCustomization,
    resetMenuConfig,
    getAvailableMenuItems
  };
};

export default useMenuConfig;
